import { AppError } from '@/core/errors/AppError.js';
import type { IPromotionRepository, PromotionListOptions, BulkPromotionStatusInput, ClonePromotionInput } from '@/features/promotion/domain/repositories/IPromotionRepository.js';
import type { PromotionRemoteDataSource } from '@/features/promotion/data/datasources/PromotionRemoteDataSource.js';
import type { PromotionScheme } from '@/features/promotion/domain/entities/PromotionScheme.js';

function throwWriteError(error: unknown): never {
  if (
    typeof error === 'object' && error !== null &&
    'code' in error && error.code === '23514' &&
    'message' in error && typeof error.message === 'string'
  ) {
    if (error.message.includes('promotion_condition_assets_unique')) {
      throw new AppError('Sản phẩm trong điều kiện áp dụng không được trùng nhau.', 'PROMOTION_CONDITION_ASSET_DUPLICATE');
    }
    if (error.message.includes('promotion_reward_assets_unique')) {
      throw new AppError('Sản phẩm trong phần thưởng không được trùng nhau.', 'PROMOTION_REWARD_ASSET_DUPLICATE');
    }
  }
  if (
    typeof error === 'object' && error !== null &&
    'code' in error && error.code === '23505' &&
    'message' in error && typeof error.message === 'string' &&
    error.message.includes('"promotion_schemes_project_code_unique"')
  ) {
    throw new AppError(
      'Mã khuyến mãi đã tồn tại trong dự án này (không phân biệt chữ hoa/thường và khoảng trắng ở hai đầu). Vui lòng chọn mã khác.',
      'PROMOTION_CODE_DUPLICATE',
    );
  }
  throw error;
}

export class PromotionRepository implements IPromotionRepository {
  constructor(private readonly dataSource: PromotionRemoteDataSource) {}

  async existsByCode(projectId: string, code: string, excludeId?: string): Promise<boolean> {
    return this.dataSource.existsByCode(projectId, code, excludeId);
  }

  bulkSetEnabled(input: BulkPromotionStatusInput): Promise<string[]> {
    return this.dataSource.bulkSetEnabled(input);
  }

  cloneScheme(input: ClonePromotionInput): Promise<PromotionScheme> {
    return this.dataSource.cloneScheme(input);
  }

  async listSchemes(options: PromotionListOptions): Promise<PromotionScheme[]> {
    return this.dataSource.listSchemes(options);
  }

  async getSchemeById(id: string): Promise<PromotionScheme | null> {
    return this.dataSource.getSchemeById(id);
  }

  async createScheme(data: Omit<PromotionScheme, 'id' | 'created_at' | 'updated_at'>): Promise<PromotionScheme> {
    try {
      return await this.dataSource.createScheme(data);
    } catch (error) {
      throwWriteError(error);
    }
  }

  async updateScheme(id: string, data: Partial<PromotionScheme>): Promise<PromotionScheme> {
    try {
      return await this.dataSource.updateScheme(id, data);
    } catch (error) {
      throwWriteError(error);
    }
  }

  async deleteScheme(id: string): Promise<void> {
    return this.dataSource.deleteScheme(id);
  }
}
